import { useState } from 'react';
import clsx from 'clsx';
import type { ControleTransactie } from '../../stores/controle-store';

interface DagDetailProps {
  datum: string;
  transacties: ControleTransactie[];
  bankExtern: number | null;
  isReadOnly: boolean;
  onMatch: (id1: string, id2: string) => void;
  onUnmatch: (id: string) => void;
}

function formatAmount(amount: number): string {
  return new Intl.NumberFormat('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount);
}

function formatTime(iso: string | null): string {
  if (!iso) return '';
  return new Date(iso).toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' });
}

function TransactieRow({
  tx,
  isSelected,
  onClick,
  onUnmatch,
  isReadOnly,
}: {
  tx: ControleTransactie;
  isSelected: boolean;
  onClick: () => void;
  onUnmatch: () => void;
  isReadOnly: boolean;
}) {
  const isMatched = tx.match_status === 'matched' || tx.match_status === 'manual_match' || tx.mollie_status === 'gecontroleerd';

  return (
    <div
      className={clsx(
        'flex items-center gap-2 px-3 py-1.5 rounded text-sm border transition-colors',
        isMatched && 'bg-emerald-50 border-emerald-200 text-emerald-800',
        !isMatched && 'bg-red-50 border-red-200 text-red-800',
        isSelected && '!bg-blue-50 !border-blue-400 ring-1 ring-blue-400',
        !isReadOnly && !isMatched && 'cursor-pointer hover:bg-red-100',
      )}
      onClick={!isReadOnly && !isMatched ? onClick : undefined}
    >
      {/* Match indicator */}
      <span className="flex-shrink-0 w-4 text-center">
        {isMatched ? (
          <svg className="w-4 h-4 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        ) : (
          <svg className="w-4 h-4 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        )}
      </span>

      {/* Amount */}
      <span className="font-mono tabular-nums font-medium min-w-[70px] text-right">
        {tx.is_refund ? '-' : ''}{formatAmount(tx.bedrag)}
      </span>

      {/* Method */}
      <span className="text-xs text-gray-500 min-w-[60px]">
        {tx.methode_detail || tx.methode}
      </span>

      {/* Details */}
      <span className="flex-1 text-xs text-gray-500 truncate">
        {tx.bron === 'sf' && (
          <>
            {tx.werk_order_naam && <span className="text-gray-700">{tx.werk_order_naam}</span>}
            {tx.ontvangen_door && <> — {tx.ontvangen_door}</>}
            {tx.opmerkingen_sf && <> — {tx.opmerkingen_sf}</>}
          </>
        )}
        {tx.bron === 'mollie' && (
          <>
            <span className="font-mono">{tx.extern_id}</span>
            {tx.datum_tijd && <> — {formatTime(tx.datum_tijd)}</>}
          </>
        )}
        {tx.bron === 'mypos' && (
          <>
            <span className="font-mono">{tx.extern_id}</span>
            {tx.methode_detail && <> — type {tx.methode_detail}</>}
          </>
        )}
      </span>

      {/* Open in SF / Mollie */}
      {tx.bron === 'sf' && (
        <a
          href={`https://cinco.my.salesforce.com/${tx.extern_id}`}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="text-gray-400 hover:text-blue-600 flex-shrink-0"
          title="Open in Salesforce"
        >
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
          </svg>
        </a>
      )}
      {tx.bron === 'mollie' && !tx.is_refund && (
        <a
          href={`https://my.mollie.com/dashboard/payments/${tx.extern_id}`}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="text-gray-400 hover:text-blue-600 flex-shrink-0"
          title="Open in Mollie"
        >
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
          </svg>
        </a>
      )}

      {/* Unmatch button */}
      {isMatched && !isReadOnly && tx.match_status === 'manual_match' && (
        <button
          onClick={(e) => { e.stopPropagation(); onUnmatch(); }}
          className="text-xs text-gray-400 hover:text-red-600 flex-shrink-0"
          title="Ontkoppelen"
        >
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      )}
    </div>
  );
}

export function DagDetail({ datum, transacties, bankExtern, isReadOnly, onMatch, onUnmatch }: DagDetailProps) {
  const [selectedSf, setSelectedSf] = useState<string | null>(null);
  const [selectedExtern, setSelectedExtern] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'pin' | 'ideal' | 'bank' | 'contant'>('pin');

  const dayTx = transacties.filter(t => t.datum === datum);

  // Pin: SF Pin vs MyPos
  const sfPinTx = dayTx.filter(t => t.bron === 'sf' && t.methode === 'pin').sort((a, b) => b.bedrag - a.bedrag);
  const myposTx = dayTx.filter(t => t.bron === 'mypos' && !t.is_refund).sort((a, b) => b.bedrag - a.bedrag);

  // iDEAL: SF iDEAL vs Mollie
  const sfIdealTx = dayTx.filter(t => t.bron === 'sf' && t.methode === 'ideal').sort((a, b) => b.bedrag - a.bedrag);
  const mollieTx = dayTx.filter(t => t.bron === 'mollie' && !t.is_refund).sort((a, b) => b.bedrag - a.bedrag);
  const refundTx = dayTx.filter(t => t.bron === 'mollie' && t.is_refund);

  // Bank: SF Bank vs imported bank transactions
  const sfBankTx = dayTx.filter(t => t.bron === 'sf' && t.methode === 'bank').sort((a, b) => b.bedrag - a.bedrag);
  const bankImportTx = dayTx.filter(t => t.bron === 'bank').sort((a, b) => b.bedrag - a.bedrag);

  // Contant: Geregistreerd vs Gecontroleerd (both from SF, split by mollie_status)
  const contantAllTx = dayTx.filter(t => t.bron === 'sf' && t.methode === 'contant').sort((a, b) => b.bedrag - a.bedrag);
  const contantGecontroleerdTx = contantAllTx.filter(t => t.mollie_status === 'gecontroleerd');
  const contantNietGecontroleerdTx = contantAllTx.filter(t => t.mollie_status !== 'gecontroleerd');

  // Auto-select tab with data or difference
  const hasPinData = sfPinTx.length > 0 || myposTx.length > 0;
  const hasIdealData = sfIdealTx.length > 0 || mollieTx.length > 0;
  const hasBankData = sfBankTx.length > 0 || bankExtern !== null || bankImportTx.length > 0;
  const hasContantData = contantAllTx.length > 0;

  const isPin = activeTab === 'pin';
  const isBank = activeTab === 'bank';
  const isContant = activeTab === 'contant';
  const sfTx = isPin ? sfPinTx : isBank ? sfBankTx : isContant ? contantNietGecontroleerdTx : sfIdealTx;
  const externTx = isPin ? myposTx : isContant ? contantGecontroleerdTx : isBank ? [] : mollieTx;
  const externLabel = isPin ? 'MyPos' : isBank ? 'Bank import' : isContant ? 'Gecontroleerd' : 'Mollie';

  const sfTotal = sfTx.reduce((s, t) => s + t.bedrag, 0);
  const externTotal = externTx.reduce((s, t) => s + t.bedrag, 0);
  const refundTotal = refundTx.reduce((s, t) => s + t.bedrag, 0);
  const unmatchedSfCount = sfTx.filter(t => t.match_status === 'unmatched').length;
  const unmatchedExternCount = externTx.filter(t => t.match_status === 'unmatched').length;
  const verschil = Math.round((externTotal - sfTotal) * 100) / 100;

  const handleSfClick = (id: string) => {
    if (selectedExtern) {
      onMatch(id, selectedExtern);
      setSelectedSf(null);
      setSelectedExtern(null);
    } else {
      setSelectedSf(selectedSf === id ? null : id);
    }
  };

  const handleExternClick = (id: string) => {
    if (selectedSf) {
      onMatch(selectedSf, id);
      setSelectedSf(null);
      setSelectedExtern(null);
    } else {
      setSelectedExtern(selectedExtern === id ? null : id);
    }
  };

  return (
    <tr>
      <td colSpan={17} className="p-0">
        <div className="bg-gray-50 border-y border-gray-200 px-4 py-3">
          {/* Tabs */}
          <div className="flex items-center gap-1 mb-3">
            {hasPinData && (
              <button
                onClick={() => { setActiveTab('pin'); setSelectedSf(null); setSelectedExtern(null); }}
                className={clsx(
                  'px-3 py-1.5 text-xs font-medium rounded-md transition-colors',
                  activeTab === 'pin'
                    ? 'bg-white text-gray-900 shadow-sm border border-gray-200'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100',
                )}
              >
                Pin / MyPos
                {sfPinTx.length > 0 && myposTx.length > 0 && Math.abs(myposTx.reduce((s, t) => s + t.bedrag, 0) - sfPinTx.reduce((s, t) => s + t.bedrag, 0)) > 1 && (
                  <span className="ml-1.5 text-red-600">!</span>
                )}
              </button>
            )}
            {hasIdealData && (
              <button
                onClick={() => { setActiveTab('ideal'); setSelectedSf(null); setSelectedExtern(null); }}
                className={clsx(
                  'px-3 py-1.5 text-xs font-medium rounded-md transition-colors',
                  activeTab === 'ideal'
                    ? 'bg-white text-gray-900 shadow-sm border border-gray-200'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100',
                )}
              >
                iDEAL / Mollie
                {sfIdealTx.length > 0 && mollieTx.length > 0 && Math.abs(mollieTx.reduce((s, t) => s + t.bedrag, 0) - sfIdealTx.reduce((s, t) => s + t.bedrag, 0)) > 1 && (
                  <span className="ml-1.5 text-red-600">!</span>
                )}
              </button>
            )}
            {hasBankData && (
              <button
                onClick={() => { setActiveTab('bank'); setSelectedSf(null); setSelectedExtern(null); }}
                className={clsx(
                  'px-3 py-1.5 text-xs font-medium rounded-md transition-colors',
                  activeTab === 'bank'
                    ? 'bg-white text-gray-900 shadow-sm border border-gray-200'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100',
                )}
              >
                Bank
                {sfBankTx.length > 0 && bankExtern !== null && Math.abs(bankExtern - sfBankTx.reduce((s, t) => s + t.bedrag, 0)) > 1 && (
                  <span className="ml-1.5 text-red-600">!</span>
                )}
              </button>
            )}
            {hasContantData && (
              <button
                onClick={() => { setActiveTab('contant'); setSelectedSf(null); setSelectedExtern(null); }}
                className={clsx(
                  'px-3 py-1.5 text-xs font-medium rounded-md transition-colors',
                  activeTab === 'contant'
                    ? 'bg-white text-gray-900 shadow-sm border border-gray-200'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100',
                )}
              >
                Contant
                {contantNietGecontroleerdTx.length > 0 && (
                  <span className="ml-1.5 text-amber-600">{contantNietGecontroleerdTx.length}</span>
                )}
              </button>
            )}
          </div>

          {/* Two-column layout */}
          <div className="grid grid-cols-2 gap-4">
            {/* Left column */}
            <div>
              <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
                {isContant ? 'Niet gecontroleerd' : `SF ${isPin ? 'Pin' : 'iDEAL'}`} ({sfTx.length}) — {formatAmount(sfTotal)}
              </h4>
              <div className="space-y-1">
                {sfTx.length === 0 && (
                  <p className="text-xs text-gray-400 py-2">
                    {isContant ? 'Alles gecontroleerd' : `Geen SF ${isPin ? 'pin' : 'iDEAL'} transacties`}
                  </p>
                )}
                {sfTx.map(tx => (
                  <TransactieRow
                    key={tx.id}
                    tx={tx}
                    isSelected={selectedSf === tx.id}
                    onClick={() => handleSfClick(tx.id)}
                    onUnmatch={() => onUnmatch(tx.id)}
                    isReadOnly={isReadOnly}
                  />
                ))}
              </div>
            </div>

            {/* Extern column (MyPos, Mollie, Bank import, or Gecontroleerd) */}
            <div>
              {isBank ? (
                <>
                  <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
                    Bank import ({bankImportTx.length}) — {bankExtern !== null ? formatAmount(bankExtern) : 'Niet geïmporteerd'}
                  </h4>
                  {bankImportTx.length > 0 ? (
                    <div className="space-y-1">
                      {bankImportTx.map(tx => (
                        <div key={tx.id} className="flex items-center gap-2 px-3 py-1.5 rounded text-sm bg-emerald-50 border border-emerald-200 text-emerald-800">
                          <svg className="w-4 h-4 text-emerald-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          <span className="font-mono tabular-nums font-medium min-w-[70px] text-right">{formatAmount(tx.bedrag)}</span>
                          <span className="flex-1 text-xs text-emerald-700 truncate" title={tx.opmerkingen_sf || ''}>
                            {tx.methode_detail || ''}
                            {tx.opmerkingen_sf && <> — {tx.opmerkingen_sf}</>}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : bankExtern !== null ? (
                    <div className="flex items-center gap-2 px-3 py-2 rounded text-sm bg-emerald-50 border border-emerald-200 text-emerald-800">
                      <svg className="w-4 h-4 text-emerald-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="font-mono tabular-nums font-medium">{formatAmount(bankExtern)}</span>
                      <span className="text-xs text-emerald-600">totaal inkomend (CAMT.053)</span>
                    </div>
                  ) : (
                    <p className="text-xs text-gray-400 py-2">Upload een bankafschrift via "Bank XML"</p>
                  )}
                </>
              ) : (
                <>
                  <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
                    {externLabel} ({externTx.length}) — {formatAmount(externTotal)}
                  </h4>
                  <div className="space-y-1">
                    {externTx.length === 0 && (
                      <p className="text-xs text-gray-400 py-2">Geen {externLabel} transacties</p>
                    )}
                    {externTx.map(tx => (
                      <TransactieRow
                        key={tx.id}
                        tx={tx}
                        isSelected={selectedExtern === tx.id}
                        onClick={() => handleExternClick(tx.id)}
                        onUnmatch={() => onUnmatch(tx.id)}
                        isReadOnly={isReadOnly}
                      />
                    ))}
                  </div>
                </>
              )}

              {/* Refunds (only for Mollie tab) */}
              {!isPin && refundTx.length > 0 && (
                <div className="mt-3">
                  <h4 className="text-xs font-medium text-red-500 uppercase tracking-wider mb-2">
                    Refunds ({refundTx.length})
                  </h4>
                  <div className="space-y-1">
                    {refundTx.map(tx => (
                      <TransactieRow
                        key={tx.id}
                        tx={tx}
                        isSelected={false}
                        onClick={() => {}}
                        onUnmatch={() => {}}
                        isReadOnly={true}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Matching hint */}
          {!isReadOnly && (unmatchedSfCount > 0 || unmatchedExternCount > 0) && (
            <p className="text-xs text-gray-500 mt-3 pt-2 border-t border-gray-200">
              Klik op een ongematchte SF transactie en dan op een {externLabel} transactie om ze te koppelen.
            </p>
          )}

          {/* Summary */}
          <div className="flex items-center gap-6 text-xs text-gray-500 mt-3 pt-2 border-t border-gray-200">
            <span>SF: <strong className="text-gray-700">{formatAmount(sfTotal)}</strong> ({sfTx.length})</span>
            <span>{externLabel}: <strong className="text-gray-700">{formatAmount(externTotal)}</strong> ({externTx.length})</span>
            <span className={clsx(Math.abs(verschil) > 1 ? 'text-red-600 font-medium' : 'text-green-600')}>
              Verschil: {formatAmount(verschil)}
            </span>
            {!isPin && refundTotal > 0 && <span className="text-red-600">Refunds: -{formatAmount(refundTotal)}</span>}
            {unmatchedSfCount > 0 && <span className="text-red-600">Unmatched SF: {unmatchedSfCount}</span>}
            {unmatchedExternCount > 0 && <span className="text-red-600">Unmatched {externLabel}: {unmatchedExternCount}</span>}
          </div>
        </div>
      </td>
    </tr>
  );
}
